import React, { Suspense, useEffect, useState } from "react";
import AudioControls from "@components/AudioControls";
import ClipboardUI from "@components/ClipboardUI/ClipboardUI";
import ContinueGame from "@components/ContinueGame";
import EndGame from "@components/EndGame";
import ErrorMessage from "@components/ErrorMessage";
import Footer from "@components/Footer/Footer";
import GameConfig from "@components/GameConfig";
import InputWidgetUI from "@components/InputWidgetUI/InputWidgetUI";
import NoAccess from "@components/NoAccess/NoAccess";
import RestartApp from "@components/RestartApp/RestartApp";
import RoomCode from "@components/RoomCode";
import Shop from "@components/Shop";
import StartMenu from "@components/StartMenu";
import StatsMonitor from "@components/Stats/Stats";
import StudioLogo from "@components/StudioLogo";
import config from '../../../package.json'
import { LoadSceneEvent, WarsEvents, WarsScenes } from "@playroomkit/pr-wars/events";
import GameLoader from "@components/GameLoader";
import BotSelectionUI from "@components/BotSelectionUI";
import LobbyControlsInstructions from "@components/LobbyControlsInstructions";
import { InGamePopupsProvider } from "@src/contexts/InGamePopupsProvider";
import { BotsProvider } from "@src/contexts/BotsProvider";
import PlayerConnectionInfo from "@components/PlayerConnectionInfo";
import TransitionUI from "@components/TransitionUI";
import DebugScene from "@components/DebugScene/DebugScene";
import LoadingContent from "@components/LoadingContent";

const DEBUG = import.meta.env.VITE_DEBUG;
const DebugMenu = DEBUG ? React.lazy(() => import('@components/DebugMenu')) : null;
const Lobby = React.lazy(() => import("@components/Lobby"));
const SelectAvatarMenu = React.lazy(() => import("@components/SelectAvatarMenu"));
const WarsPlayer = React.lazy(() => import("@components/WarsPlayer"));
const LazyWarsProvider = React.lazy(() => import("@src/contexts/LazyWarsProvider"));

interface GameContentProps {
  error: string | null;
  showLoadingBar: boolean;
  percentage: number;
  showNoAccess: boolean;
}

const GameContent = ({
  error,
  showNoAccess,
}: GameContentProps) => {

  const [scene, setScene] = useState<string | null>(null);
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [musicContext, setMusicContext] = useState<AudioContext | null>(null)

  useEffect(() => {
    const handleLoadSceneEvents = (event: Event) => {
      let e = event as LoadSceneEvent;
      setScene(e.sceneName);
    }

    document.addEventListener(WarsEvents.LoadSceneEvent, handleLoadSceneEvents);

    return () => {
      document.removeEventListener(WarsEvents.LoadSceneEvent, handleLoadSceneEvents);
    };
  }, []);

  const handleCreateGame = (roomCode: string | null, musicContext: AudioContext) => {
    setIsStarted(true);

    setRoomCode(roomCode);
    setMusicContext(musicContext);
  };

  return (
    <>
      <div className="overflow-y-hidden">
        {error && <ErrorMessage message={error} duration={3000} />}
        <StatsMonitor />
        <StudioLogo />
        <StartMenu onCreateGame={handleCreateGame} />
        
        {isStarted && !showNoAccess && (
          <Suspense fallback={<div>Loading Scene...</div>}>
            <LazyWarsProvider> 
              <BotsProvider>
                <InGamePopupsProvider>
                  {DEBUG && (
                    <Suspense fallback={<div>loading debug components...</div>}>
                      {DebugMenu && <DebugMenu />}
                    </Suspense>
                  )}
                  <GameLoader roomCode={roomCode} musicContext={musicContext!} />
                  {scene === WarsScenes.SelectAvatar && <SelectAvatarMenu />}
                  {scene === WarsScenes.Lobby && <Lobby />}
                  {scene === WarsScenes.GameShow && <WarsPlayer />}
                  <RoomCode />
                  <LobbyControlsInstructions />
                  <GameConfig />
                  <ContinueGame />
                  <EndGame />
                  <AudioControls />
                  <ClipboardUI />
                  <InputWidgetUI />
                  <BotSelectionUI />
                  <PlayerConnectionInfo />
		              <Shop />
                  <TransitionUI />
                  <DebugScene />
                  <LoadingContent />
                </InGamePopupsProvider>
              </BotsProvider>
            </LazyWarsProvider>
          </Suspense>
        )}
        
        <RestartApp />
        <Footer version={config.version} />
      </div>
      {showNoAccess && <NoAccess />}
    </>
  );
};

export default GameContent;
